/**
 * @file Geo helpers — miles/meters, great-circle distance, radius→bounds.
 *
 * Pure math with no Google dependency, so the engine can gate by radius and
 * compute distances before the SDK is ready (and so it's unit-testable).
 */
import type { Bounds, LatLng } from "./types";

export const MILES_TO_METERS = 1609.344;
const EARTH_RADIUS_MILES = 3958.7613;
const MILES_PER_DEG_LAT = 69;

export function milesToMeters(miles: number): number {
  return miles * MILES_TO_METERS;
}

export function metersToMiles(meters: number): number {
  return meters / MILES_TO_METERS;
}

/** Great-circle distance in miles (haversine). */
export function haversineMiles(a: LatLng, b: LatLng): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLng * sinLng;
  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** True when `point` is within `radiusMiles` of `center`. */
export function isWithinRadius(center: LatLng, point: LatLng, radiusMiles: number): boolean {
  return haversineMiles(center, point) <= radiusMiles;
}

/** Approximate lat/lng bounding box around a center for a given radius (miles). */
export function boundsFromRadius(center: LatLng, radiusMiles: number): Bounds {
  const latDelta = radiusMiles / MILES_PER_DEG_LAT;
  const cosLat = Math.cos((center.lat * Math.PI) / 180) || 1e-6;
  const lngDelta = radiusMiles / (MILES_PER_DEG_LAT * cosLat);
  return {
    north: center.lat + latDelta,
    south: center.lat - latDelta,
    east: center.lng + lngDelta,
    west: center.lng - lngDelta,
  };
}
